import React, { FC, useEffect, useState } from 'react';
import { Box, Icon, Input, Stack } from '@chakra-ui/react';
import { FaPhone } from 'react-icons/fa';
import getCountryFlag from 'country-flag-icons/unicode';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { COUNTRIES, getCountryTelCode } from './Countries';
import { InputGroup } from '../ui/input-group';

interface PhoneNumberInputProps {
  value?: string;
  defaultCountry: any;
  onChange: any;
  onBlur?: any;
  onClear?: any;
  placeholder: string;
  clearValue?: boolean;
  isFocused?: boolean;
  inputVariant?: React.ComponentProps<typeof Input>['variant'];
}

const FlagElement = ({ selectedCountry }) => (
  <Box>
    {selectedCountry ? (
      <Box height='1rem' mr='4px' fontSize='lg' display='flex' alignItems='center'>
        {getCountryFlag(selectedCountry?.toUpperCase())}
      </Box>
    ) : (
      <Icon color='brand.400'>
        <FaPhone />
      </Icon>
    )}
  </Box>
);

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
  value,
  defaultCountry,
  onChange,
  onBlur,
  placeholder,
  clearValue,
  isFocused,
  inputVariant,
  ...props
}) => {
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry || '');
  const [countryCode, setCountryCode] = useState(getCountryTelCode(defaultCountry) || '');
  const [number, setNumber] = useState(value || countryCode || '');
  const countryOptions = COUNTRIES.map(({ name, iso2 }) => ({
    key: name,
    label: name,
    value: iso2
  }));

  useEffect(() => {
    if (clearValue) {
      setNumber('');
    }
  }, [clearValue]);

  useEffect(() => {
    setSelectedCountry(defaultCountry);
    setCountryCode(getCountryTelCode(defaultCountry));
  }, [defaultCountry]);

  const onCountryChange = (e) => {
    const val = e.target.value;
    const code = getCountryTelCode(val);

    setCountryCode(code);
    setSelectedCountry(val);
    onChange(code);
    setNumber(code);
  };

  const onPhoneNumberChange = (e) => {
    const val = e.target.value.replace('(', '').replace(')', '').replace('-', '').replace(' ', '');
    const parsedNumber = `${countryCode}${val}`;
    // Display number with country code.
    if (!val.includes(countryCode) && val.length <= countryCode.length) setNumber(countryCode);
    else if (!val.includes(countryCode)) setNumber(parsedNumber);
    else setNumber(val);

    onChange(val);
  };

  return (
    <Stack direction={{ base: 'column', md: 'row' }} gap='2' w='full' {...props}>
      <NativeSelectRoot colorPalette='brand' w={{ base: 'full', md: '70%' }}>
        <NativeSelectField
          value={selectedCountry}
          onChange={onCountryChange}
          fontSize='sm'
          bg='bg-input'
          colorPalette='brand'
        >
          {countryOptions.map((option) => (
            <option key={option.key} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelectField>
      </NativeSelectRoot>
      <InputGroup startElement={<FlagElement selectedCountry={selectedCountry} />} w='full' {...props}>
        <Input
          variant={inputVariant}
          autoFocus={isFocused}
          // ps='2.15em'
          // pe='0'
          type='tel'
          value={number}
          placeholder={placeholder}
          onChange={onPhoneNumberChange}
          onBlur={onBlur}
          // size='lg'
          // w='full'
          // colorPalette='brand'
        />
      </InputGroup>
    </Stack>
  );
};

export default PhoneNumberInput;
